import { ref, computed, watch, Ref } from 'vue';
import { jwtDecode } from 'jwt-decode';
import { useCallback } from '@libs/use';
import { OIDC_CONFIG, userManager } from '@libs/oidc';
import type { User } from '@libs/api-reference';

type UserWithTrackerId = User & { trackerId: string };

const userStore = ref<UserWithTrackerId | null>(null);
const isLoadingUser = ref(false);

type DecodedUserToken = {
  nbf: number;
  exp: number;
  iss: string;
  aud: string[];
  client_id: string;
  sub: string;
  auth_time: number;
  idp: string;
  national_code: string;
  given_name: string;
  name: string;
  login_type: string;
  has_password: string;
  mfa_enabled: string;
  phone: string;
  register_status: string;
  tse_boursecode: string;
  tse_boursecode_id: string;
  securitiesExchangeBourseCodeBim: string;
  securitiesExchangeBourseCodeIsin: string;
  securitiesExchangeBourseCode: string;
  ime_boursecode_id: string;
  ime_boursecode: string;
  force_change_pass: string;
  last_pass_change: string;
  user_id: string;
  user_name: string;
  user_title: string;
  user_en_title: string;
  user_code: string;
  personage_id: string;
  jti: string;
  sid: string;
  iat: number;
  scope: string[];
  amr: string[];

  tracker_id: string;
};

async function decodedOIDCUserToken(): Promise<DecodedUserToken | undefined> {
  const user = await userManager.getUser();

  if (!user) return;

  return jwtDecode(user.access_token);
}

const isOIDCUserLoaded = computed(() => Boolean(userStore.value));

async function setUserByToken() {
  const userToken = await decodedOIDCUserToken();

  if (!userStore.value && !isLoadingUser.value && userToken) {
    const {
      name,
      ime_boursecode_id,
      tse_boursecode,
      national_code,
      personage_id,
      phone,
      user_code,
      user_id,
      user_name,
      user_title,
      given_name,
      tracker_id,
      securitiesExchangeBourseCodeBim: bimCode,
    } = userToken;

    userStore.value = {
      firstName: given_name,
      lastName: name.replace(given_name, ''),
      image: '',
      forceChangePassword: false,
      investorBourseCodes: [
        {
          id: Number(ime_boursecode_id),
          bourseCode: tse_boursecode,
          isActive: true,
          type: 1,
        },
      ],
      isAgent: false,
      isDealer: false,
      isFutureAgent: false,
      lastPasswordChange: false,
      nationalCode: national_code,
      permissions: [],
      personageId: Number(personage_id),
      phone,
      userCode: user_code,
      userId: Number(user_id),
      userLevel: 1,
      userName: user_name,
      userTitle: user_title,
      trackerId: tracker_id,
      bimCode,
    };
  }
}

export default function useUser({
  enabled = true,
}: { enabled?: boolean } = {}): {
  isLoadingUser: Ref<boolean>;
  onUserReady: (fn: (...arg: any[]) => any) => { off: () => void };
  user: Ref<UserWithTrackerId | null | undefined>;
  clearUser: () => void;
  isUserReady: Ref<boolean>;
  getUserInfo: () => void;
  isOIDCUserLoaded: Ref<boolean>;
} {
  const onReady = useCallback();

  /*
    Previously we call a /user/me api to get the user information.
    Then we realize, we have all the required information inside user token.
    So we decided to just load the user info from user's token and remove api call.
  */

  // simulate previous immediate way
  if (enabled) {
    setUserByToken();
  }

  watch([userStore, onReady.callback], ([user, callback]) => {
    if (user?.userName && callback) {
      onReady.trigger(user);
    }
  });

  // todo Setting authorizationToken for tradingView chart
  const authorizationData = localStorage.getItem(
    `oidc.user:${OIDC_CONFIG.authority}:${OIDC_CONFIG.client_id}`
  );

  if (authorizationData) {
    localStorage.setItem(
      'authorizationToken',
      JSON.parse(authorizationData)['access_token']
    );
  }

  const clearUser = () => {
    userStore.value = null;
  };

  return {
    isLoadingUser,
    onUserReady: onReady.on,
    user: userStore,
    clearUser,
    isUserReady: computed(() => Boolean(userStore.value)),
    getUserInfo: setUserByToken,
    isOIDCUserLoaded,
  };
}
